import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import { FiEye, FiEyeOff, FiArrowRight } from "react-icons/fi";
import "./Auth.css";

// ── Login ────────────────────────────────────────────────
export const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const [form, setForm] = useState({ emailOrPhone: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.emailOrPhone) e.emailOrPhone = "Email is required";
    if (!form.password) e.password = "Password is required";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const user = await login(form.emailOrPhone, form.password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}!`);
      const dest =
        from && from !== "/"
          ? from
          : user.role === "admin"
            ? "/admin"
            : user.role === "technician"
              ? "/technician/dashboard"
              : "/dashboard";
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <span className="logo-icon">E</span>
            <span>
              Expert<span className="logo-accent">AtDoor</span>
            </span>
          </Link>
          <h2>Welcome back</h2>
          <p>Sign in to manage your bookings</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              className={`form-input ${errors.emailOrPhone ? "error" : ""}`}
              type="text"
              placeholder="you@example.com or 9876543210"
              value={form.emailOrPhone}
              onChange={(e) =>
                setForm({ ...form, emailOrPhone: e.target.value })
              }
            />
            {errors.emailOrPhone && (
              <span className="field-error">{errors.emailOrPhone}</span>
            )}
          </div>

          <div className="form-group">
            <div className="label-row">
              <label className="form-label">Password</label>
              <Link to="/forgot-password" className="forgot-link">
                Forgot password?
              </Link>
            </div>
            <div className="input-password-wrap">
              <input
                className={`form-input ${errors.password ? "error" : ""}`}
                type={showPass ? "text" : "password"}
                placeholder="Your password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <button
                type="button"
                className="toggle-pass"
                onClick={() => setShowPass(!showPass)}
              >
                {showPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>
            {errors.password && (
              <span className="field-error">{errors.password}</span>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={loading}
          >
            {loading ? (
              "Signing in..."
            ) : (
              <>
                {" "}
                Sign In <FiArrowRight size={16} />{" "}
              </>
            )}
          </button>
        </form>

        <p className="auth-switch">
          Don't have an account? <Link to="/register">Create one</Link>
        </p>
      </div>
    </div>
  );
};

// ── Register ─────────────────────────────────────────────
export const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "customer",
  });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.match(/^\S+@\S+\.\S+$/)) e.email = "Valid email required";
    if (!form.phone.match(/^[6-9]\d{9}$/))
      e.phone = "Valid 10-digit mobile required";
    if (form.password.length < 8) e.password = "At least 8 characters";
    else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(form.password))
      e.password = "Needs upper, lower & number";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const user = await register(form);
      toast.success(`Welcome, ${user.name.split(" ")[0]}! 🎉`);
      const dest = user.role === "technician" ? "/technician/dashboard" : "/";
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <span className="logo-icon">E</span>
            <span>
              Expert<span className="logo-accent">AtDoor</span>
            </span>
          </Link>
          <h2>Create Account</h2>
          <p>Join ExpertAtDoor today — free, no OTP needed</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {/* Role toggle */}
          <div className="role-toggle">
            {["customer", "technician"].map((r) => (
              <button
                key={r}
                type="button"
                className={`role-btn ${form.role === r ? "active" : ""}`}
                onClick={() => setForm({ ...form, role: r })}
              >
                {r === "customer"
                  ? "🏠 I need services"
                  : "🧹 I provide services"}
              </button>
            ))}
          </div>

          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input
              className={`form-input ${errors.name ? "error" : ""}`}
              type="text"
              placeholder="Your full name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            {errors.name && <span className="field-error">{errors.name}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                className={`form-input ${errors.email ? "error" : ""}`}
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              {errors.email && (
                <span className="field-error">{errors.email}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                className={`form-input ${errors.phone ? "error" : ""}`}
                type="tel"
                placeholder="10-digit mobile"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              {errors.phone && (
                <span className="field-error">{errors.phone}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div className="input-password-wrap">
              <input
                className={`form-input ${errors.password ? "error" : ""}`}
                type={showPass ? "text" : "password"}
                placeholder="Min 8 chars, upper+lower+number"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <button
                type="button"
                className="toggle-pass"
                onClick={() => setShowPass(!showPass)}
              >
                {showPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>
            {errors.password && (
              <span className="field-error">{errors.password}</span>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={loading}
          >
            {loading ? (
              "Creating account..."
            ) : (
              <>
                {" "}
                Create Account <FiArrowRight size={16} />{" "}
              </>
            )}
          </button>
        </form>

        <p className="auth-switch">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

// ── Forgot Password ──────────────────────────────────────
export const ForgotPasswordPage = () => {
  const { forgotPassword, resetPassword } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState("email"); // 'email' | 'reset'
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [errors, setErrors] = useState({});

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const startCooldown = () => setCooldown(30);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.match(/^\S+@\S+\.\S+$/)) {
      setErrors({ email: "Valid email required" });
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      await forgotPassword(email);
      toast.success("If that email is registered, a code is on its way.");
      setStep("reset");
      startCooldown();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Could not send code. Try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      await forgotPassword(email);
      toast.success("Code resent. Check your inbox.");
      startCooldown();
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not resend code.");
    } finally {
      setResending(false);
    }
  };

  const validateReset = () => {
    const e = {};
    if (!otp.match(/^\d{6}$/)) e.otp = "Enter the 6-digit code";
    if (newPassword.length < 8) e.newPassword = "At least 8 characters";
    else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword))
      e.newPassword = "Needs upper, lower & number";
    if (confirmPassword !== newPassword)
      e.confirmPassword = "Passwords do not match";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!validateReset()) return;
    setLoading(true);
    try {
      await resetPassword({ email, otp, newPassword });
      toast.success("Password reset! Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "Reset failed. Check your code and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <span className="logo-icon">E</span>
            <span>
              Expert<span className="logo-accent">AtDoor</span>
            </span>
          </Link>
          <h2>{step === "email" ? "Forgot password?" : "Reset password"}</h2>
          <p>
            {step === "email"
              ? "Enter your email and we'll send you a verification code"
              : `Enter the 6-digit code sent to ${email}`}
          </p>
        </div>

        {step === "email" ? (
          <form className="auth-form" onSubmit={handleSendCode} noValidate>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                className={`form-input ${errors.email ? "error" : ""}`}
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
              {errors.email && (
                <span className="field-error">{errors.email}</span>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={loading}
            >
              {loading ? (
                "Sending code..."
              ) : (
                <>
                  {" "}
                  Send Code <FiArrowRight size={16} />{" "}
                </>
              )}
            </button>
          </form>
        ) : (
          <form className="auth-form" onSubmit={handleResetSubmit} noValidate>
            <div className="form-group">
              <label className="form-label">Verification Code</label>
              <div className="otp-input-wrap">
                <input
                  className={`form-input otp-input ${errors.otp ? "error" : ""}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  autoFocus
                />
              </div>
              {errors.otp && <span className="field-error">{errors.otp}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">New Password</label>
              <div className="input-password-wrap">
                <input
                  className={`form-input ${errors.newPassword ? "error" : ""}`}
                  type={showPass ? "text" : "password"}
                  placeholder="Min 8 chars, upper+lower+number"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="toggle-pass"
                  onClick={() => setShowPass(!showPass)}
                >
                  {showPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
              {errors.newPassword && (
                <span className="field-error">{errors.newPassword}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                className={`form-input ${errors.confirmPassword ? "error" : ""}`}
                type={showPass ? "text" : "password"}
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {errors.confirmPassword && (
                <span className="field-error">{errors.confirmPassword}</span>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={loading}
            >
              {loading ? (
                "Resetting..."
              ) : (
                <>
                  {" "}
                  Reset Password <FiArrowRight size={16} />{" "}
                </>
              )}
            </button>

            <button
              type="button"
              className="resend-btn"
              onClick={handleResend}
              disabled={resending || cooldown > 0}
            >
              {cooldown > 0
                ? `Resend code in ${cooldown}s`
                : resending
                  ? "Resending..."
                  : "Didn't get a code? Resend"}
            </button>
          </form>
        )}

        <p className="auth-switch">
          Remembered your password? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
};
